import React, { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import Toggle from '../ui/Toggle';
import {
  STORE_TYPES,
  saveStoreConnection,
  type StoreConnectionDoc,
  type StoreType,
} from '../../lib/store';

interface StoreConnectionModalProps {
  open: boolean;
  connection?: StoreConnectionDoc | null;
  onClose: () => void;
  onSaved: () => void;
}

export function StoreConnectionModal({
  open,
  connection,
  onClose,
  onSaved,
}: StoreConnectionModalProps) {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [storeType, setStoreType] = useState<StoreType>('woocommerce');
  const [storeUrl, setStoreUrl] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  // Credentials states
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (connection) {
      setName(connection.name);
      setStoreType(connection.storeType);
      setStoreUrl(connection.storeUrl);
      setStatus(connection.status === 'active' ? 'active' : 'inactive');

      setConsumerKey(connection.config?.consumerKey ? '••••••••' : '');
      setConsumerSecret(connection.config?.consumerSecret ? '••••••••' : '');
      setAccessToken(connection.config?.accessToken ? '••••••••' : '');
      setClientId(connection.config?.clientId ? '••••••••' : '');
      setClientSecret(connection.config?.clientSecret ? '••••••••' : '');
      setWebhookSecret(connection.config?.webhookSecret ? '••••••••' : '');
    } else {
      setName('');
      setStoreType('woocommerce');
      setStoreUrl('');
      setStatus('active');

      setConsumerKey('');
      setConsumerSecret('');
      setAccessToken('');
      setClientId('');
      setClientSecret('');
      setWebhookSecret('');
    }
    setError(null);
  }, [connection, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !storeUrl.trim()) {
      setError(t('store.connections.nameAndUrlRequired'));
      return;
    }

    const config: Record<string, any> = {};
    if (storeType === 'woocommerce') {
      if (consumerKey) config.consumerKey = consumerKey.trim();
      if (consumerSecret) config.consumerSecret = consumerSecret.trim();
      if (webhookSecret) config.webhookSecret = webhookSecret.trim();
    } else if (storeType === 'shopify') {
      if (accessToken) config.accessToken = accessToken.trim();
      if (webhookSecret) config.webhookSecret = webhookSecret.trim();
    } else if (storeType === 'salla') {
      if (clientId) config.clientId = clientId.trim();
      if (clientSecret) config.clientSecret = clientSecret.trim();
      if (webhookSecret) config.webhookSecret = webhookSecret.trim();
    }

    setSubmitting(true);
    setError(null);
    try {
      await saveStoreConnection({
        id: connection?.id,
        name: name.trim(),
        storeType,
        storeUrl: storeUrl.trim(),
        status,
        config,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save store connection');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={connection ? t('store.connections.edit') : t('store.connections.create')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.connections.platform')} *
            </label>
            <select
              value={storeType}
              onChange={(e) => setStoreType(e.target.value as StoreType)}
              disabled={Boolean(connection)}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            >
              {STORE_TYPES.map((st) => (
                <option key={st} value={st}>
                  {st === 'woocommerce' ? 'WooCommerce (WordPress)' : st.charAt(0).toUpperCase() + st.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('store.connections.storeName')} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('store.connectionNamePlaceholder') || 'e.g. My Oman Store'}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('store.connections.storeUrl')} *
          </label>
          <input
            type="url"
            required
            value={storeUrl}
            onChange={(e) => setStoreUrl(e.target.value)}
            placeholder="https://mystore.om"
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-line p-3 dark:border-slate-700">
          <div>
            <span className="block text-xs font-semibold text-ink dark:text-white">
              {t('store.connections.status')}
            </span>
            <span className="text-[11px] text-ink-3 dark:text-slate-400">
              {status === 'active' ? t('status.active') : t('store.connections.inactive')}
            </span>
          </div>
          <Toggle
            on={status === 'active'}
            onChange={(v) => setStatus(v ? 'active' : 'inactive')}
            label={t('store.connections.status')}
          />
        </div>

        {/* WooCommerce Specific Setup + Download Plugin */}
        {storeType === 'woocommerce' && (
          <div className="space-y-3 rounded-xl border border-line bg-surface-subtle p-3.5 dark:border-slate-700 dark:bg-slate-850">
            <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3 dark:border-brand-900 dark:bg-brand-950/40">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h5 className="text-xs font-bold text-brand dark:text-brand-tint">
                    {t('store.wc.pluginTitle')}
                  </h5>
                  <p className="mt-0.5 text-[11px] text-brand-800 dark:text-brand-300">
                    {t('store.wc.pluginDesc')}
                  </p>
                </div>
                <a
                  href="/downloads/watag-abandoned-cart.zip"
                  download
                  className="inline-flex items-center gap-1 rounded-lg bg-brand px-2.5 py-1.5 text-xs font-bold text-white hover:bg-brand-600 dark:bg-brand dark:text-white flex-shrink-0"
                >
                  📥 {t('store.wc.downloadPlugin')}
                </a>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Consumer Key
              </label>
              <input
                type="password"
                value={consumerKey}
                onChange={(e) => setConsumerKey(e.target.value)}
                placeholder={connection?.config?.consumerKey ? '••••••••' : 'ck_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Consumer Secret
              </label>
              <input
                type="password"
                value={consumerSecret}
                onChange={(e) => setConsumerSecret(e.target.value)}
                placeholder={connection?.config?.consumerSecret ? '••••••••' : 'cs_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Webhook Secret
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={connection?.config?.webhookSecret ? '••••••••' : 'secret_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        )}

        {/* Shopify Specific Setup */}
        {storeType === 'shopify' && (
          <div className="space-y-3 rounded-xl border border-line bg-surface-subtle p-3.5 dark:border-slate-700 dark:bg-slate-850">
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Admin API Access Token
              </label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={connection?.config?.accessToken ? '••••••••' : 'shpat_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Webhook Signature Secret
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={connection?.config?.webhookSecret ? '••••••••' : 'secret_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        )}

        {/* Salla Specific Setup */}
        {storeType === 'salla' && (
          <div className="space-y-3 rounded-xl border border-line bg-surface-subtle p-3.5 dark:border-slate-700 dark:bg-slate-850">
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={connection?.config?.clientId ? '••••••••' : 'client_id'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Client Secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={connection?.config?.clientSecret ? '••••••••' : 'client_secret'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                Webhook Secret
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={connection?.config?.webhookSecret ? '••••••••' : 'secret_...'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        )}

        {connection && (
          <div className="rounded-lg border border-line p-3 text-xs dark:border-slate-700">
            <span className="block font-semibold text-ink-3 dark:text-slate-400">
              {t('store.connections.webhookEndpoint')}:
            </span>
            <code className="mt-1 block break-all font-mono text-[11px] text-brand dark:text-brand-tint">
              {`https://wapp.tagnologys.com/store/webhook/${connection.tenantId || '{tenantId}'}/${connection.id}`}
            </code>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
